import React, { useState, useEffect } from 'react';

import { Heading } from '../../components';

import { FaUser, FaEnvelope, FaPhoneAlt, FaCalendar, FaClock, FaUsers, FaSuitcase, FaCreditCard, FaAngleDown, FaComment, FaCar } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';

import axios from 'axios';



// API endpoint for submitting the booking form
const API_PATH = 'https://driveon-react.asdesignsgalaxy.com/api/phpmailer/booking.php';

// Regex patterns for email and phone number validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const BookingForm = ({ formType }) => {
  // State to manage form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    pickupLocation: '',
    dropoffLocation: '',
    pickupDate: '',
    pickupTime: '',
    dropoffDate: '',
    dropoffTime: '',
    passengers: '',
    luggage: '',
    carType: '',
    paymentMethod: '',
    instructions: '',
    extraServices: []
  });

  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const handleSubmitForm = async (e) => {

    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];

      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.pickupLocation.trim()) validationErrors.push('Pickup location is required');
      if (!formData.dropoffLocation.trim()) validationErrors.push('Dropoff location is required');
      if (!formData.pickupDate.trim()) validationErrors.push('Pickup date is required');
      if (!formData.pickupTime.trim()) validationErrors.push('Pickup time is required');
      if (!formData.dropoffDate.trim()) validationErrors.push('Dropoff date is required');
      if (!formData.dropoffTime.trim()) validationErrors.push('Dropoff time is required');
      if (!formData.passengers) validationErrors.push('Number of passengers is required');
      if (!formData.luggage) validationErrors.push('Luggage option is required');
      if (!formData.carType) validationErrors.push('Car type is required');
      if (!formData.paymentMethod) validationErrors.push('Payment method is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(item => postData.append(`${key}[]`, item));
        } else {
          postData.append(key, value);
        }
      });

      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Booking request submission failed');
      }

      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        pickupLocation: '',
        dropoffLocation: '',
        pickupDate: '',
        pickupTime: '',
        dropoffDate: '',
        dropoffTime: '',
        passengers: '',
        luggage: '',
        carType: '',
        paymentMethod: '',
        instructions: '',
        extraServices: []
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      if (checked) {
        return { ...prev, extraServices: [...prev.extraServices, value] };
      } else {
        return { ...prev, extraServices: prev.extraServices.filter(item => item !== value) };
      }
    });
  };

    // Define reusable input field components as variables
    const inputFields = {
      name: (
        <div className="input-field">
        <label htmlFor="name"><FaUser /></label>
        <input type="text" name="name" className="box" id="name" 
          value={formData.name} onChange={handleInputChange}
          placeholder="Name" disabled={isSubmitting} required />
      </div>
      ),
      email: (
        <div className="input-field">
        <label htmlFor="email"><FaEnvelope /></label>
        <input type="email" name="email" className="box" id="email" 
          value={formData.email} onChange={handleInputChange}
          placeholder="Email" disabled={isSubmitting} required />
      </div>
      ),
      phone: (
        <div className="input-field">
          <label htmlFor="number"><FaPhoneAlt /></label>
          <input type="number" name="number" className="box" id="number" 
            value={formData.number} onChange={handleInputChange}
            placeholder="Phone Number" disabled={isSubmitting} required />
        </div>
      ),
      pickupLocation: (
        <div className="input-field">
          <label htmlFor="pickup-location"><FaLocationDot /></label>
          <input type="text" name="pickupLocation" className="box" id="pickup-location" 
            value={formData.pickupLocation} onChange={handleInputChange}
            placeholder="Pickup Location" disabled={isSubmitting} required />
        </div>
      ),
      dropoffLocation: (
        <div className="input-field">
          <label htmlFor="dropoff-location"><FaLocationDot /></label>
          <input type="text" name="dropoffLocation" className="box" id="dropoff-location" 
            value={formData.dropoffLocation} onChange={handleInputChange}
            placeholder="Dropoff Location" disabled={isSubmitting} required />
        </div>
      ),
      pickupDate: (
        <div className="input-field">
          <label htmlFor="pickup-date"><FaCalendar /></label>
          <input type="text" name="pickupDate" placeholder="Pickup Date" id="pickup-date" className="box" 
            value={formData.pickupDate} onChange={handleInputChange}
            onFocus={(e) => (e.target.type = 'date')}
            onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
        </div>
      ),
      pickupTime: (
        <div className="input-field">
          <label htmlFor="pickup-time"><FaClock /></label>
          <input type="text" name="pickupTime" placeholder="Pickup Time" id="pickup-time" className="box" 
            value={formData.pickupTime} onChange={handleInputChange}
            onFocus={(e) => (e.target.type = 'time')}
            onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
        </div>
      ),
      dropoffDate: (
        <div className="input-field">
          <label htmlFor="dropoff-date"><FaCalendar /></label>
          <input type="text" name="dropoffDate" placeholder="Dropoff Date" id="dropoff-date" className="box" 
            value={formData.dropoffDate} onChange={handleInputChange}
            onFocus={(e) => (e.target.type = 'date')}
            onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
        </div>
      ),
      dropoffTime: (
        <div className="input-field">
          <label htmlFor="dropoff-time"><FaClock /></label>
          <input type="text" name="dropoffTime" placeholder="Dropoff Time" id="dropoff-time" className="box" 
            value={formData.dropoffTime} onChange={handleInputChange}
            onFocus={(e) => (e.target.type = 'time')}
            onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
        </div>
      ),
      passengers: (
        <div className="input-field">
          <label htmlFor="passengers"><FaUsers /></label>
          <input type="number" name="passengers" className="box" id="passengers" 
            value={formData.passengers} onChange={handleInputChange}
            placeholder="No of Passengers" disabled={isSubmitting} required />
        </div>
      ),
      luggage: (
        <div className="select">
        <div className="select-container">
          <label htmlFor="luggage"><FaSuitcase /></label>
          <select className="select-box" id="luggage" name="luggage"
            value={formData.luggage} onChange={handleInputChange}
            disabled={isSubmitting} required>
            <option value="">Luggage</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
          <div className="icon-container"><FaAngleDown className="icon" /></div>
        </div>
      </div>
      ),
      carType: (
        <div className="select">
          <div className="select-container">
            <label htmlFor="car-type"><FaCar /></label>
            <select className="select-box" id="car-type" name="carType"
              value={formData.carType} onChange={handleInputChange}
              disabled={isSubmitting} required>
              <option value="">Car Type</option>
              <option value="Hatchback">Hatchback</option>
              <option value="Sedan">Sedan</option>
              <option value="Coupe">Coupe</option>
              <option value="Suv">SUV</option>
              <option value="Sports Car">Sports Car</option>
            </select>
            <div className="icon-container"><FaAngleDown className="icon" /></div>
          </div>
        </div>
      ),
      paymentMethod: (
        <div className="select">
          <div className="select-container">
            <label htmlFor="payment-method"><FaCreditCard /></label>
            <select className="select-box" id="payment-method" name="paymentMethod"
              value={formData.paymentMethod} onChange={handleInputChange}
              disabled={isSubmitting} required>
              <option value="">Payment Method</option>
              <option value="credit-card">Credit Card</option>
              <option value="cash">Cash</option>
              <option value="mobile-payment">Mobile Payment</option>
            </select>
            <div className="icon-container"><FaAngleDown className="icon" /></div>
          </div>
        </div>
      ),
      instructions: (
        <div className="input-field text-area">
          <label htmlFor="instructions"><FaComment /></label>
          <textarea cols="30" rows="10" name="instructions" className="box" id="instructions" 
            value={formData.instructions} onChange={handleInputChange}
            placeholder="Special Instructions" disabled={isSubmitting} required></textarea>
        </div>
      ),
    };
  
    // Define reusable extra services checkboxes as variables
    const extraServicesCheckboxes = (
      <div className="extra-services">

        {/* Baby Seat Checkbox */}
        <div className="checkbox-label">
          <input type="checkbox" id="baby-seat" name="extra-services" value="baby-seat"
            checked={formData.extraServices.includes('baby-seat')}
            onChange={handleCheckboxChange} disabled={isSubmitting} />
          <label htmlFor="baby-seat">Baby Seat</label>
          <div className="checkbox-indicator"></div>
        </div>

        {/* Driver Checkbox */}
        <div className="checkbox-label">
          <input type="checkbox" id="driver" name="extra-services" value="driver"
            checked={formData.extraServices.includes('driver')}
            onChange={handleCheckboxChange} disabled={isSubmitting} />
          <label htmlFor="driver">Driver</label>
          <div className="checkbox-indicator"></div>
        </div>

      </div>
    );
  
    // Define reusable submit button component
    const submitButton = (
      <button type="submit" className="btn" name="submit" id="submit-1" disabled={isSubmitting}>
        {isSubmitting ? 'Submitting...' : 'Rent Car'}
      </button>
    );

  // Render the form structure based on the `formType` prop
  const renderFormStructure = () => {
    if (formType === 'booking-page') {
      return (
        <>

          {/* Form heading */}
          <Heading title={'Request a Booking'} subTitle={'Book Your Ride'} sideHeading linear />

          <div className="input-box">

            {inputFields.name}  {/* Name Input */}
            {inputFields.email} {/* Email Input */}

          </div>

          <div className="input-box">

            {inputFields.phone} {/* Phone Number Input */}
            {inputFields.pickupLocation} {/* Pickup Location Input */}

          </div>

          <div className="input-box">
            {inputFields.dropoffLocation} {/* Dropoff Location Input */}
          </div>

          <div className="input-box">

            {inputFields.pickupDate} {/* Pickup Date Input */}
            {inputFields.pickupTime} {/* Pickup Time Input */}

          </div>

          <div className="input-box">

            {inputFields.dropoffDate} {/* Dropoff Date Input */}
            {inputFields.dropoffTime} {/* Dropoff Time Input */}

          </div>

          <div className="input-box">

            {inputFields.passengers} {/* Number of Passengers Input */}
            {inputFields.luggage} {/* Luggage Option Select */}

          </div>

          <div className="input-box">

            {inputFields.carType} {/* Car Type Select */}
            {inputFields.paymentMethod} {/* Payment Method Select */}

          </div>

          {inputFields.instructions} {/* Instructions Text Area */}

          {extraServicesCheckboxes} {/* Extra Services Checkboxes */}

          {submitButton} {/* Submit Button */}

        </>
      );
    } else if (formType === 'home-booking') {
      return (
        <>

          {/* Form heading */}
          <Heading title={'Request a Booking'} subTitle={'Book Your Ride'} sideHeading linear />

          <div className="input-box">
            {inputFields.name}  {/* Name Input */}
            {inputFields.email} {/* Email Input */}
            {inputFields.phone} {/* Phone Number Input */}
            {inputFields.pickupLocation} {/* Pickup Location Input */}
          </div>

          <div className="input-box">

            {inputFields.dropoffLocation} {/* Dropoff Location Input */}
            {inputFields.pickupDate} {/* Pickup Date Input */}
            {inputFields.pickupTime} {/* Pickup Time Input */}
            {inputFields.dropoffDate} {/* Dropoff Date Input */}

          </div>

          <div className="input-box">

            {inputFields.dropoffTime} {/* Dropoff Time Input */}
            {inputFields.passengers} {/* Number of Passengers Input */}
            {inputFields.luggage} {/* Luggage Option Select */}

          </div>

          <div className="input-box">

            {inputFields.carType} {/* Car Type Select */}
            {inputFields.paymentMethod} {/* Payment Method Select */}
            <div className='form-btn'>{submitButton}</div> {/* Submit Button */}

          </div>

          {extraServicesCheckboxes} {/* Extra Services Checkboxes */}

        </>
      );
    }
  };

  return (
    <form className="dark-form" onSubmit={handleSubmitForm}>

      {renderFormStructure()}

      {error && (
        <div className="alert error">
          {error.split(' | ').map((msg, i) => (
            <span key={i}>{msg}</span>
          ))}
        </div>
      )}

      {mailSent && <div className="alert success"><span>Request submitted</span></div>}

    </form>
  );

};

export default BookingForm;